import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dimension, Capacity, RelationTarget, SyntaxchecksData } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getBaseColor, Mode } from "./colorUtils";

const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

type Props = {
  capacities: Capacity[];
  onChange: (caps: Capacity[]) => void;
  dimensions: Dimension[];
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
  syntaxData?: SyntaxchecksData | null;
};

type FlatItem = {
  type: "tag" | "point" | "dimension";
  id: string;
  name: string;
  dimId: string;
};

function buildFlatItems(targets: RelationTarget[], dimensions: Dimension[]): FlatItem[] {
  const items: FlatItem[] = [];
  targets.forEach((target) => {
    const dim = dimensions.find(d => d.id === target.dimensionId);
    if (!dim) return;

    if (target.mode === "all") {
      if (!items.some(x => x.type === "dimension" && x.dimId === dim.id)) {
        items.push({ type: "dimension", id: dim.id, name: dim.name, dimId: dim.id });
      }
    } else {
      target.tagIds?.forEach(tagId => {
        const tag = dim.tags.find(t => t.id === tagId);
        if (tag && !items.some(x => x.type === "tag" && x.id === tag.id)) {
          items.push({ type: "tag", id: tag.id, name: tag.name, dimId: dim.id });
        }
      });
      target.pointIds?.forEach(pointId => {
        const point = dim.points.find(p => p.id === pointId);
        if (point && !items.some(x => x.type === "point" && x.id === point.id)) {
          items.push({ type: "point", id: point.id, name: point.name, dimId: dim.id });
        }
      });
    }
  });
  return items;
}

function rebuildTargets(items: FlatItem[]): RelationTarget[] {
  const targets: RelationTarget[] = [];
  items.forEach((item) => {
    if (item.type === "dimension") {
      targets.push({
        dimensionId: item.dimId,
        mode: "all",
        tagIds: [],
        pointIds: [],
      });
      return;
    }
    let existing = targets.find(t => t.dimensionId === item.dimId && t.mode !== "all");
    if (!existing) {
      existing = { dimensionId: item.dimId, mode: "points", tagIds: [], pointIds: [] };
      targets.push(existing);
    }
    if (item.type === "tag" && !existing.tagIds.includes(item.id)) {
      existing.tagIds.push(item.id);
      if (existing.pointIds.length === 0) existing.mode = "tags";
      else existing.mode = "points";
    } else if (item.type === "point" && !existing.pointIds.includes(item.id)) {
      existing.pointIds.push(item.id);
      existing.mode = "points";
    }
  });
  return targets;
}

function pickDropEffect(effectAllowed: string | undefined): "copy" | "move" {
  if (!effectAllowed) return "copy";
  const allowed = effectAllowed.toLowerCase();
  if (allowed === "copy") return "copy";
  if (allowed.includes("copy")) return "copy";
  return "move";
}

function SortableCapacityCard({
  capacity,
  expandedId,
  setExpandedId,
  onUpdate,
  onDelete,
  dimensions,
  userHue,
  mode,
  allNames,
  syntaxStat,
}: {
  capacity: Capacity;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<Capacity>) => void;
  onDelete: (id: string) => void;
  dimensions: Dimension[];
  userHue: number;
  mode: Mode;
  allNames: string[];
  syntaxStat?: { syntax?: string; SyntaxMessages?: string[] };
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: capacity.id });

  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: getBaseColor(4, "connection", userHue, mode),
  };

  const expanded = expandedId === capacity.id;
  const isDuplicate = allNames.filter((n) => n === capacity.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);

  const backendErrors = syntaxStat?.SyntaxMessages;
  const hasBackendError = backendErrors && backendErrors.length > 0;
  const errorMessage = hasBackendError
    ? (Array.isArray(backendErrors) ? backendErrors.map(e => String(e)).join(", ") : String(backendErrors))
    : null;
  const isValid = !errorMessage;
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  const forItems = buildFlatItems(capacity.for || [], dimensions);
  const whereItems = buildFlatItems(capacity.where || [], dimensions);

  // Wird während eines Drags gesetzt: true = Drop landete in einer gültigen Zone
  const droppedInZoneRef = useRef(false);
  // Globaler dragover-Handler: sorgt dafür dass onDragEnd sofort feuert (macOS-Fix)
  const globalDragOverRef = useRef<((e: DragEvent) => void) | null>(null);

  const buttonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    background: mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    borderRadius: 14,
    fontSize: "14px",
    fontWeight: 500,
    color: mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
    cursor: "grab",
    userSelect: "none",
    transition: "all 0.15s ease",
  };

  const removeTag = (field: "for" | "where", tagId: string) => {
    const targets = capacity[field];
    const newTargets = targets.map((t) => {
      if (t.tagIds?.includes(tagId)) return { ...t, tagIds: t.tagIds.filter(id => id !== tagId) };
      if (t.mode === "all") {
        const dim = dimensions.find(d => d.id === t.dimensionId);
        if (!dim) return t;
        return {
          ...t,
          mode: "points" as const,
          tagIds: dim.tags.map(tag => tag.id).filter(id => id !== tagId),
          pointIds: dim.points.map(point => point.id),
        };
      }
      return t;
    }).filter(t => t.pointIds.length > 0 || t.tagIds.length > 0 || t.mode === "all");
    onUpdate(capacity.id, { [field]: newTargets });
  };

  const removePoint = (field: "for" | "where", pointId: string) => {
    const targets = capacity[field];
    const newTargets = targets.map((t) => {
      if (t.pointIds?.includes(pointId)) return { ...t, pointIds: t.pointIds.filter(id => id !== pointId) };
      if (t.mode === "all") {
        const dim = dimensions.find(d => d.id === t.dimensionId);
        if (!dim) return t;
        return {
          ...t,
          mode: "points" as const,
          tagIds: dim.tags.map(tag => tag.id),
          pointIds: dim.points.map(point => point.id).filter(id => id !== pointId),
        };
      }
      return t;
    }).filter(t => t.pointIds.length > 0 || t.tagIds.length > 0 || t.mode === "all");
    onUpdate(capacity.id, { [field]: newTargets });
  };

  const removeDimension = (field: "for" | "where", dimId: string) => {
    const newTargets = capacity[field].filter(t => t.dimensionId !== dimId);
    onUpdate(capacity.id, { [field]: newTargets });
  };

  const removeItem = (field: "for" | "where", item: FlatItem) => {
    if (item.type === "tag") removeTag(field, item.id);
    else if (item.type === "point") removePoint(field, item.id);
    else if (item.type === "dimension") removeDimension(field, item.dimId);
  };

  const addToTarget = (field: "for" | "where", dimId: string, type: "tag" | "point", id: string) => {
    const targets = capacity[field];
    const existingIdx = targets.findIndex(t => t.dimensionId === dimId);

    if (existingIdx !== -1) {
      const newTargets = targets.map((target, i) => {
        if (i !== existingIdx) return target;
        if (type === "point") {
          const nextPoints = (target.mode === "points" ? target.pointIds : []).includes(id) ? target.pointIds : [...(target.mode === "points" ? target.pointIds : []), id];
          return { ...target, mode: "points" as const, pointIds: nextPoints, tagIds: [] };
        } else {
          const nextTags = (target.mode === "tags" ? target.tagIds : []).includes(id) ? target.tagIds : [...(target.mode === "tags" ? target.tagIds : []), id];
          return { ...target, mode: "tags" as const, tagIds: nextTags, pointIds: [] };
        }
      });
      onUpdate(capacity.id, { [field]: newTargets });
    } else {
      onUpdate(capacity.id, {
        [field]: [...targets, {
          dimensionId: dimId,
          mode: type === "tag" ? "tags" : "points",
          tagIds: type === "tag" ? [id] : [],
          pointIds: type === "point" ? [id] : [],
        }]
      });
    }
  };

  const addDimension = (field: "for" | "where", dimId: string) => {
    const exists = capacity[field].some(t => t.dimensionId === dimId);
    if (!exists) {
      onUpdate(capacity.id, {
        [field]: [...capacity[field], { dimensionId: dimId, mode: "all" as const, tagIds: [], pointIds: [] }]
      });
    } else {
      onUpdate(capacity.id, {
        [field]: capacity[field].map(t => t.dimensionId !== dimId ? t : { ...t, mode: "all" as const, tagIds: [], pointIds: [] })
      });
    }
  };

  const reorderItem = (field: "for" | "where", dragged: FlatItem, target: FlatItem) => {
    const items = buildFlatItems(capacity[field], dimensions);
    const dragIdx = items.findIndex(i => i.type === dragged.type && i.id === dragged.id);
    const targetIdx = items.findIndex(i => i.type === target.type && i.id === target.id);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;
    const reordered = [...items];
    if (dragged.dimId !== target.dimId) {
      const draggedItems = reordered.filter(i => i.dimId === dragged.dimId);
      const remaining = reordered.filter(i => i.dimId !== dragged.dimId);
      const removedBeforeTarget = reordered.slice(0, targetIdx).filter((i) => i.dimId === dragged.dimId).length;
      remaining.splice(Math.max(0, targetIdx - removedBeforeTarget), 0, ...draggedItems);
      onUpdate(capacity.id, { [field]: rebuildTargets(remaining) });
      return;
    }
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    onUpdate(capacity.id, { [field]: rebuildTargets(reordered) });
  };

  const DropZone = ({ label, field, items }: { label: string, field: "for" | "where", items: FlatItem[] }) => {
    const otherField: "for" | "where" = field === "for" ? "where" : "for";

    const moveToThisField = (draggedItem: FlatItem) => {
      // Quell-Feld: Item entfernen
      const computeRemoved = (targets: RelationTarget[]): RelationTarget[] => {
        if (draggedItem.type === "dimension") {
          return targets.filter((t) => t.dimensionId !== draggedItem.dimId);
        }
        if (draggedItem.type === "tag") {
          return targets
            .map((t) => {
              if (t.tagIds?.includes(draggedItem.id)) return { ...t, tagIds: t.tagIds.filter(id => id !== draggedItem.id) };
              if (t.mode === "all") {
                const dim = dimensions.find(d => d.id === t.dimensionId);
                if (!dim) return t;
                return { ...t, mode: "points" as const, tagIds: dim.tags.map(tg => tg.id).filter(id => id !== draggedItem.id), pointIds: dim.points.map(p => p.id) };
              }
              return t;
            })
            .filter(t => t.pointIds.length > 0 || t.tagIds.length > 0 || t.mode === "all");
        }
        // point
        return targets
          .map((t) => {
            if (t.pointIds?.includes(draggedItem.id)) return { ...t, pointIds: t.pointIds.filter(id => id !== draggedItem.id) };
            if (t.mode === "all") {
              const dim = dimensions.find(d => d.id === t.dimensionId);
              if (!dim) return t;
              return { ...t, mode: "points" as const, tagIds: dim.tags.map(tg => tg.id), pointIds: dim.points.map(p => p.id).filter(id => id !== draggedItem.id) };
            }
            return t;
          })
          .filter(t => t.pointIds.length > 0 || t.tagIds.length > 0 || t.mode === "all");
      };

      // Ziel-Feld: Item hinzufügen
      const computeAdded = (targets: RelationTarget[]): RelationTarget[] => {
        if (draggedItem.type === "dimension") {
          const exists = targets.some(t => t.dimensionId === draggedItem.dimId);
          if (!exists) return [...targets, { dimensionId: draggedItem.dimId, mode: "all", tagIds: [], pointIds: [] }];
          return targets.map(t => t.dimensionId !== draggedItem.dimId ? t : { ...t, mode: "all" as const, tagIds: [], pointIds: [] });
        }
        const existingIdx = targets.findIndex(t => t.dimensionId === draggedItem.dimId);
        if (existingIdx !== -1) {
          return targets.map((t, i) => {
            if (i !== existingIdx) return t;
            if (draggedItem.type === "point") {
              const pointIds = t.mode === "points" ? t.pointIds : [];
              return { ...t, mode: "points" as const, pointIds: pointIds.includes(draggedItem.id) ? pointIds : [...pointIds, draggedItem.id], tagIds: [] };
            }
            const tagIds = t.mode === "tags" ? t.tagIds : [];
            return { ...t, mode: "tags" as const, tagIds: tagIds.includes(draggedItem.id) ? tagIds : [...tagIds, draggedItem.id], pointIds: [] };
          });
        }
        return [
          ...targets,
          {
            dimensionId: draggedItem.dimId,
            mode: draggedItem.type === "tag" ? "tags" as const : "points" as const,
            tagIds: draggedItem.type === "tag" ? [draggedItem.id] : [],
            pointIds: draggedItem.type === "point" ? [draggedItem.id] : [],
          },
        ];
      };

      // Ein einziger onUpdate-Aufruf für beide Felder gleichzeitig
      onUpdate(capacity.id, {
        [otherField]: computeRemoved(capacity[otherField]),
        [field]: computeAdded(capacity[field]),
      });
    };

    return (
      <div style={{ marginBottom: 12 }}>
        <div
          onDragOver={(e) => {
            const hasOwn = e.dataTransfer.types.includes(`application/x-capacity-${field}-delete`);
            const hasForeign = e.dataTransfer.types.includes(`application/x-capacity-${otherField}-delete`);
            if (e.dataTransfer.types.includes("application/x-zeitgeist") || hasOwn || hasForeign) {
              e.preventDefault();
              e.currentTarget.style.borderColor = "#4ade80";
              e.currentTarget.style.background = mode === "dark" ? "rgba(74, 222, 128, 0.12)" : "rgba(74, 222, 128, 0.08)";
            }
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
            e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
          }}
          onDrop={(e) => {
            e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
            e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
            // Reorder innerhalb derselben Zone – Pills behandeln das selbst
            if (e.dataTransfer.types.includes(`application/x-capacity-${field}-delete`)) {
              e.preventDefault(); e.stopPropagation();
              droppedInZoneRef.current = true;
              return;
            }
            // Cross-Zone: Item von otherField hierher verschieben
            const crossRaw = e.dataTransfer.getData(`application/x-capacity-${otherField}-delete`);
            if (crossRaw) {
              e.preventDefault();
              e.stopPropagation();
              droppedInZoneRef.current = true;
              try {
                const dragged: FlatItem & { kind: string; field: string } = JSON.parse(crossRaw);
                if (dragged.kind === "capacity-reorder" && dragged.field === otherField) {
                  moveToThisField(dragged);
                }
              } catch { /* ignore */ }
              return;
            }
            // Drag aus dem Dimension-Panel (externe Quelle)
            const data = e.dataTransfer.getData("application/x-zeitgeist");
            if (!data) return;
            e.preventDefault(); e.stopPropagation();
            try {
              const payload = JSON.parse(data);
              if (payload.kind === "create-condition-from-dimension-point") addToTarget(field, payload.dimensionId, "point", payload.pointId);
              else if (payload.kind === "create-condition-from-dimension-tag") addToTarget(field, payload.dimensionId, "tag", payload.tagId);
              else if (payload.kind === "create-condition-from-dimension") addDimension(field, payload.dimensionId);
            } catch { /* ignore */ }
          }}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", minHeight: 46,
            border: mode === "dark" ? "1px dashed rgba(255,255,255,0.2)" : "1px dashed rgba(0,0,0,0.15)",
            borderRadius: 8, background: mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
            transition: "all 0.15s ease",
            position: "relative",
            flexWrap: "wrap",
          }}
        >
          <span style={{
            fontSize: "0.7em",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)",
            minWidth: 42,
            flexShrink: 0,
            marginRight: 6,
          }}>
            {label}
          </span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
            {items.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                draggable
                onDragStart={(e) => {
                  droppedInZoneRef.current = false;
                  // Globaler dragover-Handler: Browser akzeptiert Drop überall →
                  // onDragEnd feuert sofort ohne macOS-Verzögerungsanimation
                  const handler = (ev: DragEvent) => { ev.preventDefault(); };
                  globalDragOverRef.current = handler;
                  document.addEventListener("dragover", handler);
                  const payload = { type: item.type, id: item.id, dimId: item.dimId, name: item.name, kind: "capacity-reorder", field };
                  e.dataTransfer.setData(`application/x-capacity-${field}-delete`, JSON.stringify(payload));
                  e.dataTransfer.setData("text/plain", JSON.stringify(payload));
                  e.dataTransfer.effectAllowed = "move";
                  (e.currentTarget as HTMLElement).style.opacity = "0.4";
                }}
                onDragEnd={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                  if (globalDragOverRef.current) {
                    document.removeEventListener("dragover", globalDragOverRef.current);
                    globalDragOverRef.current = null;
                  }
                  if (!droppedInZoneRef.current) removeItem(field, item);
                }}
                onDragOver={(e) => {
                  const hasOwn = e.dataTransfer.types.includes(`application/x-capacity-${field}-delete`);
                  const hasForeign = e.dataTransfer.types.includes(`application/x-capacity-${otherField}-delete`);
                  if (hasOwn || hasForeign) {
                    e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "move";
                    (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                    (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                  }
                }}
                onDragLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                }}
                onDrop={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                  droppedInZoneRef.current = true;
                  // Cross-Zone: Item von otherField auf dieses Pill droppen → verschieben
                  const crossRaw = e.dataTransfer.getData(`application/x-capacity-${otherField}-delete`);
                  if (crossRaw) {
                    e.preventDefault(); e.stopPropagation();
                    try {
                      const dragged: FlatItem & { kind: string; field: string } = JSON.parse(crossRaw);
                      if (dragged.kind === "capacity-reorder" && dragged.field === otherField) {
                        moveToThisField(dragged);
                      }
                    } catch { /* ignore */ }
                    return;
                  }
                  // Reorder innerhalb derselben Zone
                  const raw = e.dataTransfer.getData(`application/x-capacity-${field}-delete`) || e.dataTransfer.getData("text/plain");
                  if (!raw) return;
                  e.preventDefault(); e.stopPropagation();
                  try {
                    const dragged = JSON.parse(raw);
                    if (dragged.kind !== "capacity-reorder" || dragged.field !== field) return;
                    if (dragged.type === item.type && dragged.id === item.id) return;
                    reorderItem(field, dragged, item);
                  } catch { /* ignore */ }
                }}
                style={buttonStyle}
              >
                {item.name}
              </div>
            ))}
            {items.length === 0 && <span style={{ color: "rgba(128,128,128,0.5)", fontStyle: "italic", fontSize: "0.9em" }}>Drag points here</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...cardStyle,
        margin: "8px 0",
        padding: 8,
        borderRadius: 6,
        color: "inherit",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
      {...attributes}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div {...listeners} style={{ cursor: "grab" }} title="Drag to reorder · Drag out to remove">☰</div>
        <span style={{ cursor: "pointer", padding: "0 6px", userSelect: "none" }} onClick={() => setExpandedId(expanded ? null : capacity.id)}>
          {expanded ? "▾" : "▸"}
        </span>
        {isEditingName ? (
           <input
             type="text"
             value={capacity.name}
             onChange={(e) => {
               const stripped = stripSpecialChars(e.target.value);
               onUpdate(capacity.id, { name: stripped });
             }}
             onBlur={() => setIsEditingName(false)}
             onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
             autoFocus
             className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
             style={{ flex: 1 }}
           />
        ) : (
          <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
            {capacity.name}
          </div>
        )}
        {/* Syntax Validation Warning */}
        {errorMessage && (
          <span
            title={errorMessage}
            style={{ fontSize: "1.2em", cursor: "help", animation: "pulse 2s infinite" }}
          >
            {validationIcon}
          </span>
        )}
        {/* Capacity löschen – entfernt per Drag-out */}
      </div>
      {expanded && (
        <div style={{ marginTop: 12, width: "100%", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap", width: "100%", boxSizing: "border-box" }}>
            <select
              value={capacity.type}
              onChange={(e) => onUpdate(capacity.id, { type: e.target.value as Capacity["type"] })}
              style={{ padding: "6px 10px", borderRadius: 6, opacity: 0.8, background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", border: "1px solid rgba(128,128,128,0.3)", minWidth: 0 }}
            >
              <option value="limit">Limit</option>
            </select>
            <input 
              type="number" 
              value={capacity.limitValue} 
              onChange={e => onUpdate(capacity.id, { limitValue: parseInt(e.target.value) || 0 })}
              style={{ width: 80, maxWidth: "100%", padding: "6px 10px", borderRadius: 6, border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)", background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", boxSizing: "border-box" }} 
            />
          </div>
          {/* Scope + Mode */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <label style={{ fontSize: "0.95em", fontWeight: 500 }}>Scope:</label>
            <select
              value={capacity.scope || "always"}
              onChange={(e) => onUpdate(capacity.id, { scope: e.target.value as Capacity["scope"] })}
              style={{ padding: "6px 10px", borderRadius: 6, border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)", background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", fontSize: "0.95em" }}
            >
              <option value="always">always</option>
              <option value="period">period</option>
              <option value="day">day</option>
              <option value="template">template</option>
            </select>
            {(capacity.scope && capacity.scope !== "always") && (
              <>
                <label style={{ fontSize: "0.95em", fontWeight: 500 }}>Mode:</label>
                <select
                  value={capacity.mode || "any"}
                  onChange={(e) => onUpdate(capacity.id, { mode: e.target.value as Capacity["mode"] })}
                  style={{ padding: "6px 10px", borderRadius: 6, border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)", background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)", color: "inherit", fontSize: "0.95em" }}
                >
                  <option value="any">any</option>
                  <option value="shortterm">shortterm</option>
                  <option value="longterm">longterm</option>
                </select>
              </>
            )}
          </div>
          <DropZone label="FOR" field="for" items={forItems} />
          <DropZone label="WHERE" field="where" items={whereItems} />
          {/* Validation Error Banner */}
          {errorMessage && (
            <div className="syntax-error-banner">
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CapacityEditor({ capacities, onChange, dimensions, allNames, isActive, toggle, userHue, mode, projectId, syntaxData }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeCapId, setActiveCapId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }));

  const capacitySyntaxMap = useMemo(() => {
    const map = new Map<string, { syntax?: string; SyntaxMessages: string[] }>();
    const arr = Array.isArray(syntaxData) ? syntaxData : [];
    arr.forEach((entry: any) => {
      const name = entry?.capacityName;
      if (name) {
        map.set(name, {
          syntax: entry.syntax,
          SyntaxMessages: entry.message ? [entry.message] : [],
        });
      }
    });
    return map;
  }, [syntaxData]);

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => { if (e.relatedTarget === null) document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true })); };
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => window.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  const handleAdd = () => {
    const newCap: Capacity = { id: Date.now().toString(), name: "Capacity" + (capacities.length + 1), type: "limit", limitValue: 1, for: [], where: [] };
    onChange([...capacities, newCap]);
    setExpandedId(newCap.id);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleUpdate = (id: string, updates: Partial<Capacity>) => {
    onChange(capacities.map(c => c.id === id ? { ...c, ...updates } : c));
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleDelete = (id: string) => {
    onChange(capacities.filter(c => c.id !== id));
    if (expandedId === id) setExpandedId(null);
    notifyProjectActivity(projectId).catch(() => {});
  };

  return (
    <div
      className="capacity-editor"
      ref={editorFrameRef}
      style={{
        marginTop: "1em",
        marginBottom: "1em",
        padding: "0.5em",
        borderRadius: 8,
        background: getBaseColor(2, "section", userHue, mode),
      }}
    >
      <div
        className="capacity-editor__header"
        onClick={toggle}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong className="capacity-editor__title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isActive ? "▾" : "▸"} Capacities
        </strong>
      </div>
      {isActive && (
        <div className="capacity-editor__body" style={{ marginTop: "0.5em" }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              setExpandedId(null);
              setActiveCapId(String(e.active.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveCapId(null);

              // Drag-out-to-delete: Element komplett außerhalb des Editors → löschen
              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect && editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                handleDelete(String(active.id));
                return;
              }

              if (active.id !== over?.id) {
                const oldIndex = capacities.findIndex(c => c.id === active.id);
                const newIndex = capacities.findIndex(c => c.id === over?.id);
                onChange(arrayMove(capacities, oldIndex, newIndex));
              }
            }}
          >
            <SortableContext items={capacities.map(c => c.id)} strategy={verticalListSortingStrategy}>
              {capacities.map(c => (
                <SortableCapacityCard key={c.id} capacity={c} expandedId={expandedId} setExpandedId={setExpandedId} onUpdate={handleUpdate} onDelete={handleDelete} dimensions={dimensions} userHue={userHue} mode={mode} allNames={allNames} syntaxStat={capacitySyntaxMap.get(c.name)} />
              ))}
            </SortableContext>

            {/* DragOverlay – Ghost-Karte beim Ziehen */}
            <DragOverlay>
              {activeCapId ? (
                <div
                  className="drag-overlay-card"
                  style={{
                    background: getBaseColor(4, "connection", userHue, mode),
                  }}
                >
                  <div className="editor-card__drag">☰</div>
                  <div style={{ flex: 1 }}>
                    {capacities.find(c => c.id === activeCapId)?.name ?? ""}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
          {capacities.length === 0 && <div style={{ color: mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", fontStyle: "italic", fontSize: "0.9em", padding: "8px 4px" }}>No capacities defined.</div>}
          <button
            onClick={handleAdd}
            className="capacity-editor__add"
          >
            + Capacity
          </button>
        </div>
      )}
    </div>
  );
}
